import { Component } from 'react';

import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { useRouterActions } from 'react-resource-router';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import {
  BuildStatus as BkBuildStatus,
  BuildStatusProps as BkBuildStatusProps,
  LazyBuildStatus as BkLazyBuildStatus,
  LazyBuildStatusProps as BkLazyBuildStatusProps,
} from '@atlassian/bitkit-builds';
import { IconSizes } from '@atlassian/bitkit-icon';

import { BuildStatus as BuildStatusType } from 'src/components/types';
import { showFlag } from 'src/redux/flags';
import { BuildStatusesMap } from 'src/types';
import authRequest from 'src/utils/fetch';

type BuildFailureMessages = {
  buildFailureTitle: FormattedMessage.MessageDescriptor;
  buildFailureDescription: FormattedMessage.MessageDescriptor;
};

type Props = {
  commitStatuses?: BuildStatusType[];
  buildCounts?: BuildStatusesMap;
  showError: (messages: BuildFailureMessages) => {};
  statusUrl: string;
  buildFailureTitle: FormattedMessage.MessageDescriptor;
  buildFailureDescription: FormattedMessage.MessageDescriptor;
  showIcon?: boolean;
  iconSize?: IconSizes;
};

const BkBuildStatusWithRouter = (props: BkBuildStatusProps) => {
  const { push } = useRouterActions();

  return <BkBuildStatus {...props} routerPush={push} />;
};

const BkLazyBuildStatusWithRouter = (props: BkLazyBuildStatusProps) => {
  const { push } = useRouterActions();

  return <BkLazyBuildStatus {...props} routerPush={push} />;
};

class BuildStatusConnected extends Component<Props> {
  loadBuilds = async () => {
    const { buildFailureTitle, buildFailureDescription } = this.props;
    try {
      const response = await fetch(authRequest(this.props.statusUrl));
      if (!response.ok) {
        throw new Error();
      }
      const json = await response.json();
      return json.values;
    } catch {
      this.props.showError({ buildFailureTitle, buildFailureDescription });
      return [];
    }
  };

  render() {
    const { commitStatuses, buildCounts, iconSize, showIcon } = this.props;
    const ignoreStoppedBuildStatus = FeatureGates.checkGate(
      StatsigFeatureKeys.prListIgnoreStoppedBuildStatus
    );

    if (commitStatuses && commitStatuses.length) {
      return (
        <BkBuildStatusWithRouter
          showIcon={showIcon}
          iconSize={iconSize}
          builds={commitStatuses}
          ignoreStoppedBuildStatus={ignoreStoppedBuildStatus}
        />
      );
    }

    if (buildCounts) {
      return (
        <BkLazyBuildStatusWithRouter
          buildCounts={buildCounts}
          loadBuilds={this.loadBuilds}
          showIcon={showIcon}
          iconSize={iconSize}
          ignoreStoppedBuildStatus={ignoreStoppedBuildStatus}
        />
      );
    }

    return null;
  }
}

const mapDispatchToProps = {
  showError: (messages: BuildFailureMessages) =>
    showFlag({
      type: 'error',
      id: 'build-status',
      title: { msg: messages.buildFailureTitle },
      description: { msg: messages.buildFailureDescription },
      autoDismiss: true,
    }),
};

export default connect(null, mapDispatchToProps)(BuildStatusConnected);
